import PropTypes from 'prop-types';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  TextField,
  Select,
  MenuItem,
  Box,
  FormControl,
  InputLabel,
  Alert,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import React, { useState } from 'react';
import { getDateINYMD } from '../../../utils/formatTime';

export default function DateSelectorModal({
  openDateSelectorModal,
  handleClose,
  onApply,
  isInterval,
  maxDays
}) {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedTimeRange, setSelectedTimeRange] = useState('hour');
  const [errors, setErrors] = useState({});

  const handleModalClose = () => {
    setStartDate(null);
    setEndDate(null);
    if (isInterval) setSelectedTimeRange('hour');
    setErrors({});
    handleClose();
  };

  const handleStartDateChange = (date) => {
    setStartDate(date);

    if (date && endDate) {
      const diff = endDate.diff(date, 'day');

      const allowedDays = maxDays || (isInterval ? 7 : null);
      if (allowedDays && diff > allowedDays) {
        setErrors({ dateRange: `Date range should be ${allowedDays} days or less` });
      } else if (diff < 0) {
        setErrors({ dateRange: 'End Date should not be before Start Date' });
      } else {
        setErrors({});
      }
    } else {
      setErrors({});
    }
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);

    if (date && startDate) {
      const diff = date.diff(startDate, 'day');

      const allowedDays = maxDays || (isInterval ? 7 : null);
      if (allowedDays && diff > allowedDays) {
        setErrors({ dateRange: `Date range should be ${allowedDays} days or less` });
      } else if (diff < 0) {
        setErrors({ dateRange: 'End Date should not be before Start Date' });
      } else {
        setErrors({});
      }
    } else {
      setErrors({});
    }
  };

  const handleTimeRangeChange = (event) => {
    setSelectedTimeRange(event.target.value);
    setErrors({});
  };

  const validateFields = () => {
    const newErrors = {};

    if (!startDate) newErrors.startDate = 'Start Date Required';
    if (!endDate) newErrors.endDate = 'End Date Required';

    if (isInterval && !selectedTimeRange) {
      newErrors.interval = 'Interval Required';
    }

    if (startDate && endDate) {
      const diff = endDate.diff(startDate, 'day');

      const allowedDays = maxDays || (isInterval ? 7 : null);
      if (allowedDays && diff > allowedDays) {
        newErrors.dateRange = `Date range should be ${allowedDays} days or less`;
      }
      if (diff < 0) {
        newErrors.dateRange = 'End Date should not be before Start Date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleApply = () => {
    if (!validateFields()) return;

    const payload = {
      startDate: getDateINYMD(startDate.toDate()),
      endDate: getDateINYMD(endDate.toDate())
    };

    if (isInterval) {
      payload.interval = selectedTimeRange;
    }

    onApply?.(payload);
    handleModalClose();
  };

  const isApplyDisabled = () => {
    if (!startDate || !endDate) return true;

    if (isInterval && !selectedTimeRange) return true;

    const diff = endDate.diff(startDate, 'day');

    if (diff < 0) return true;
    const allowedDays = maxDays || (isInterval ? 7 : null);
    if (allowedDays && diff > allowedDays) return true;

    return false;
  };

  const getMinDate = () => (isInterval ? dayjs().subtract(7, 'day') : dayjs().subtract(500, 'day'));

  const getMaxEndDate = () => {
    if (!startDate) return dayjs();
    const allowedDays = maxDays || (isInterval ? 7 : null);
    if (!allowedDays) return dayjs(); // no restriction

    const maxDate = startDate.add(allowedDays, 'day');
    return maxDate.isAfter(dayjs()) ? dayjs() : maxDate;
  };

  return (
    <Dialog open={openDateSelectorModal} onClose={handleModalClose}>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          fontWeight: 600,
          pb: 0
        }}
      >
        Select Date Range {isInterval ? 'and Interval' : ''}
        <IconButton edge="end" onClick={handleClose} aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box sx={{ px: 3, pt: 0, pb: 1 }}>
        <Typography variant="caption" color="#79797F" sx={{ fontSize: '12px' }}>
          {isInterval
            ? 'Select a date range from today going back up to 7 days.'
            : maxDays
              ? `Select a date range up to ${maxDays} days apart.`
              : 'Select any date range.'}
        </Typography>
      </Box>

      <DialogContent>
        <DialogContentText>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {errors.dateRange && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {errors.dateRange}
                </Alert>
              )}

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={handleStartDateChange}
                    minDate={getMinDate()}
                    maxDate={dayjs()}
                    format="DD-MM-YYYY"
                    slots={{ textField: TextField }}
                    slotProps={{
                      textField: {
                        error: !!errors.startDate,
                        helperText: errors.startDate
                      }
                    }}
                  />
                </Box>

                <Box sx={{ flex: 1 }}>
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={handleEndDateChange}
                    minDate={startDate || getMinDate()}
                    maxDate={getMaxEndDate()}
                    format="DD-MM-YYYY"
                    slots={{ textField: TextField }}
                    slotProps={{
                      textField: {
                        error: !!errors.endDate,
                        helperText: errors.endDate
                      }
                    }}
                  />
                </Box>

                {isInterval && (
                  <FormControl sx={{ minWidth: 120 }} error={!!errors.interval}>
                    <InputLabel>Interval</InputLabel>
                    <Select
                      value={selectedTimeRange}
                      onChange={handleTimeRangeChange}
                      label="Interval"
                    >
                      <MenuItem value="hour">Hour</MenuItem>
                      <MenuItem value="minute">Minute</MenuItem>
                      <MenuItem value="day">Day</MenuItem>
                    </Select>
                    {errors.interval && (
                      <Typography variant="caption" color="error" sx={{ ml: 1.5 }}>
                        {errors.interval}
                      </Typography>
                    )}
                  </FormControl>
                )}
              </Box>
            </Box>
          </LocalizationProvider>
        </DialogContentText>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleModalClose} sx={{ mb: 2, borderRadius: 2 }}>
          Cancel
        </Button>
        <Button
          onClick={handleApply}
          sx={{ mr: 2, mb: 2, borderRadius: 2 }}
          variant="contained"
          disabled={isApplyDisabled()}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}

DateSelectorModal.propTypes = {
  openDateSelectorModal: PropTypes.bool,
  handleClose: PropTypes.func,
  onApply: PropTypes.func,
  isInterval: PropTypes.bool,
  maxDays: PropTypes.number
};
